import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { PanelCollapseToggle, panelBodyId } from '../ui/CollapsibleSection'

interface SplitPaneProps {
  initialTopPct: number
  storageKey: string
  minTopPct?: number
  maxTopPct?: number
  /**
   * Collapse the bottom half to a slim strip (#189). Dragging cannot express
   * this — topPct is hard-clamped to [minTopPct, maxTopPct] — so it is a
   * separate flag, and the persisted split number is deliberately left
   * untouched so expanding restores the ratio the operator last dragged to.
   *
   * Consumers that never pass it (BlockConfiguratorRoute) render exactly as
   * before: same divider, same clamping, same drag/keyboard behaviour.
   */
  collapsedBottom?: boolean
  /** Expand handler for the collapsed strip's toggle. */
  onExpandBottom?: () => void
  /** Already-translated name of the bottom panel, shown in the strip. */
  bottomLabel?: string
  /** Panel id backing the strip toggle's testid + `aria-controls`. */
  bottomPanelId?: string
  /**
   * The owner's `usePanelCollapsed().toggleRef`. The strip's toggle is the
   * node that replaces the collapsed pane's own toggle, so it takes over its
   * focus (and hands it back on expand).
   */
  bottomToggleRef?: Ref<HTMLButtonElement>
  children: [ReactNode, ReactNode]
}

function readStored(storageKey: string, fallback: number, min: number, max: number): number {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw == null) return fallback
    const n = Number(raw)
    if (!Number.isFinite(n)) return fallback
    if (n < min || n > max) return fallback
    return n
  } catch {
    return fallback
  }
}

function persist(storageKey: string, value: number): void {
  try {
    localStorage.setItem(storageKey, String(value))
  } catch {
    // ignore (private mode, quota)
  }
}

export function SplitPane({
  initialTopPct,
  storageKey,
  minTopPct = 20,
  maxTopPct = 80,
  collapsedBottom = false,
  onExpandBottom,
  bottomLabel,
  bottomPanelId = 'split-bottom',
  bottomToggleRef,
  children,
}: SplitPaneProps) {
  const clamp = useCallback(
    (n: number) => Math.min(maxTopPct, Math.max(minTopPct, n)),
    [minTopPct, maxTopPct]
  )

  const [topPct, setTopPct] = useState<number>(() =>
    readStored(storageKey, initialTopPct, minTopPct, maxTopPct)
  )
  const containerRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    draggingRef.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      if (rect.height === 0) return
      const next = clamp(((e.clientY - rect.top) / rect.height) * 100)
      setTopPct(next)
    },
    [clamp]
  )

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return
      draggingRef.current = false
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        // ignore (pointer no longer captured)
      }
      persist(storageKey, topPct)
    },
    [storageKey, topPct]
  )

  const onDoubleClick = useCallback(() => {
    const next = clamp(initialTopPct)
    setTopPct(next)
    persist(storageKey, next)
  }, [clamp, initialTopPct, storageKey])

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null
      switch (e.key) {
        case 'ArrowUp':
          next = clamp(topPct - 5)
          break
        case 'ArrowDown':
          next = clamp(topPct + 5)
          break
        case 'Home':
          next = minTopPct
          break
        case 'End':
          next = maxTopPct
          break
        case 'Enter':
          next = clamp(initialTopPct)
          break
        default:
          return
      }
      e.preventDefault()
      setTopPct(next)
      persist(storageKey, next)
    },
    [clamp, initialTopPct, minTopPct, maxTopPct, storageKey, topPct]
  )

  useEffect(() => {
    setTopPct(readStored(storageKey, initialTopPct, minTopPct, maxTopPct))
  }, [storageKey, initialTopPct, minTopPct, maxTopPct])

  return (
    <div ref={containerRef} className="flex h-full min-h-0 flex-col">
      <div
        className="min-h-0 overflow-hidden"
        // Collapsed: the top half takes everything the strip doesn't. `topPct`
        // is still whatever was persisted, ready for the expand.
        style={{ flex: collapsedBottom ? '1 1 0%' : `${topPct} 1 0%` }}
      >
        {children[0]}
      </div>
      {collapsedBottom ? (
        // No divider while collapsed: there is nothing to resize, and a
        // draggable separator with no second pane reports a meaningless
        // aria-valuenow.
        <div
          data-testid="split-collapsed-bottom"
          className="flex shrink-0 items-center gap-1.5 border-t border-stone-200 bg-white px-3 py-1.5 dark:border-stone-800 dark:bg-stone-900"
        >
          <PanelCollapseToggle
            collapsed
            onToggle={onExpandBottom ?? (() => {})}
            panelLabel={bottomLabel ?? ''}
            controls={panelBodyId(bottomPanelId)}
            testId={`${bottomPanelId}-toggle`}
            expandedChevron="down"
            buttonRef={bottomToggleRef}
          />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {bottomLabel}
          </span>
        </div>
      ) : (
        <>
          <div
            data-testid="preview-split-divider"
            role="separator"
            tabIndex={0}
            aria-orientation="horizontal"
            aria-valuenow={Math.round(topPct)}
            aria-valuemin={minTopPct}
            aria-valuemax={maxTopPct}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            className="h-1.5 cursor-ns-resize bg-stone-200 hover:bg-stone-300 focus:bg-stone-400 focus:outline-none dark:bg-stone-700 dark:hover:bg-stone-600 dark:focus:bg-stone-500"
          />
          <div className="min-h-0 overflow-hidden" style={{ flex: `${100 - topPct} 1 0%` }}>
            {children[1]}
          </div>
        </>
      )}
    </div>
  )
}
